import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import getVideoId from "get-video-id";
import moment from "moment";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TeacherContext } from "../../context/TeacherContext";
import styled from "styled-components";
import { SButton, SContainer, SError, SFormControl } from "../../components/BasicComponents";
import AppNavbar from "../../components/Navbars/AppNavbar";
import YouTubePlayer from "../../components/YouTubePlayer/YouTubePlayer";
import Loading from "../../components/Loading/Loading";
import { te } from "../../utils/errorsTranslation";

const NewLecturePage = (props) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.new" });

  const { courses, getCourses, getCourseInfo, createLiveLecture, createFlippedLecture, loading, error, resetError } =
    useContext(TeacherContext);
  const [course, setCourse] = useState(null);
  const [name, setName] = useState(t("lecture_name", { date: new Date() }));
  const [fileData, setFileData] = useState(null);
  const [type, setType] = useState("1");
  const [startTime, setStartTime] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [endTime, setEndTime] = useState(moment().add(24, "hours").format("YYYY-MM-DDTHH:mm"));
  const [videoUrl, setVideoUrl] = useState("");

  const history = useHistory();

  useEffect(() => {
    if (props.match.path !== "/new-lecture/:courseUUID") {
      history.push("/error");
    }

    const courseUUID = props.match.params.courseUUID;
    getCourses().then((courses) => {
      if (courses) {
        getCourseInfo(courseUUID).then((course) => setCourse(course));
      }
    });

    return () => resetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreate = (e) => {
    e.preventDefault();
    //Get video id
    const videoId = getVideoId(videoUrl).id;
    //Send the request to create a new lecture
    if (type === "2") {
      createLiveLecture(course.uuid, {
        name,
        videoId,
        startTime,
        endTime,
        published: true,
        fileData: fileData ?? undefined,
      }).then((lecture) => {
        if (lecture) history.push(`/teacher/lecture/${lecture.uuid}`);
      });
    } else {
      createFlippedLecture(course.uuid, {
        name,
        videoId,
        published: true,
        slides: fileData ?? undefined,
      }).then((lecture) => {
        if (lecture) history.push(`/teacher/lecture/${lecture.uuid}`);
      });
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <SContainer className="d-flex flex-column" fluid fill="true" dark="true">
          <Row>
            <AppNavbar dark="true" />
          </Row>

          <Styles>
            <Form onSubmit={onCreate}>
              <Container className="mt-5 h-100">
                <h1 className="fw-bolder ms-2">{t("title")}</h1>
                <Row>
                  <Col className="pe-5" lg={6} sm={12}>
                    <SFormSelect className="fs-4" defaultValue={type} onChange={(e) => setType(e.target.value)}>
                      <option className="text-uppercase" value={1}>
                        {t("flipped")}
                      </option>
                      <option className="text-uppercase" value={2}>
                        {t("live")}
                      </option>
                    </SFormSelect>
                    {course && courses && (
                      <SFormSelect
                        className="fs-4 my-4"
                        defaultValue={course?.uuid}
                        onChange={(e) => setCourse(courses[e.target.value])}
                      >
                        <option value={course.uuid}>{course.name}</option>
                        {Object.values(courses)
                          .filter((c) => c.uuid !== course.uuid)
                          .map((course) => (
                            <option key={course.uuid} value={course.uuid}>
                              {course.name}
                            </option>
                          ))}
                      </SFormSelect>
                    )}
                    <SFormControl
                      required
                      className="fs-4 my-4"
                      type="text"
                      placeholder="Title"
                      defaultValue={t("lecture_name", { date: new Date() })}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <SFormControl
                      className="fs-4 my-4"
                      type="file"
                      accept="application/pdf"
                      hidden-value="true"
                      onChange={(e) => setFileData(e.target.files[0])}
                    />
                    {type === "2" && (
                      <>
                        <SFormControl
                          className="fs-4 my-2"
                          required
                          type="datetime-local"
                          defaultValue={moment().format("YYYY-MM-DDTHH:mm")}
                          onChange={(e) => setStartTime(moment(e.target.value).format("YYYY-MM-DDTHH:mm"))}
                        />
                        <SFormControl
                          required
                          className="fs-4 my-4"
                          type="datetime-local"
                          min={startTime}
                          defaultValue={moment().add(24, "hours").format("YYYY-MM-DDTHH:mm")}
                          onChange={(e) => setEndTime(moment(e.target.value).format("YYYY-MM-DDTHH:mm"))}
                        />
                      </>
                    )}
                    {type === "1" && (
                      <SFormControl
                        className="fs-4"
                        type="text"
                        placeholder={t("link")}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        required
                      />
                    )}
                  </Col>
                  <Col lg={6} sm={12} className="d-flex flex-column flex-grow-1 justify-content-center my-4">
                    {type === "2" && (
                      <SFormControl
                        className="fs-4 mb-4"
                        type="text"
                        placeholder="YouTube Link"
                        onChange={(e) => setVideoUrl(e.target.value)}
                        required
                      />
                    )}
                    <SVideoPlayer className="mb-4">
                      <YouTubePlayer videoId={getVideoId(videoUrl).id} />
                    </SVideoPlayer>
                    <SButton type="submit" dark="true" className="mt-4 w-100 fs-5">
                      {t("submit")}
                    </SButton>
                  </Col>
                </Row>
                {error && (
                  <Row className="mb-4">
                    <Col>
                      <SError>{te(error.message)}</SError>
                    </Col>
                  </Row>
                )}
              </Container>
            </Form>
          </Styles>
        </SContainer>
      )}
    </>
  );
};

const Styles = styled.div`
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;

  .container {
    height: 100%;

    color: var(--text-primary-dark) !important;
    .col {
      height: 100%;
    }
  }
`;

const SFormSelect = styled(Form.Select)`
  border-radius: 99em;
  border: none;
  box-shadow: none;

  padding: 0.5em 1.5em;

  background-color: var(--secondary-dark);
  color: var(--text-primary-dark);

  ::placeholder {
    text-transform: uppercase;
    color: var(--text-secondary-dark);
  }

  :focus {
    box-shadow: none;
    background-color: var(--secondary-dark);
    color: var(--text-primary-dark);
  }
`;

const SVideoPlayer = styled.div`
  position: relative;

  border-radius: 2em;
  border: none;
  box-shadow: none;

  padding: 0.5em 1.5em;
  width: 100%;
  min-height: 20rem;

  overflow: hidden;

  background-color: var(--secondary-dark);
  color: var(--text-primary-dark);

  ::placeholder {
    text-transform: uppercase;
    color: var(--text-secondary-dark);
  }

  :focus {
    box-shadow: none;
    background-color: var(--secondary-dark);
    color: var(--text-primary-dark);
  }

  iframe {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 100%;
    width: 100%;
    transform: translate(-50%, -50%);
  }
`;

export default NewLecturePage;
